import i18n from '@/i18n'

const addToObjectKey = (obj, key, type, text) => {
  if (!(key in obj)) {
    obj[key] = []
  }

  obj[key].push({ type, text })
}

const getI18nProductsParams = (zoneId, productId1, productId2, pricingRatio, weight, zoneList, GET_PRODUCT) => {
  const zone = zoneList?.find(z => z.id === zoneId)
  const descKey1 = GET_PRODUCT?.(productId1)?.desc_key || productId1
  const descKey2 = GET_PRODUCT?.(productId2)?.desc_key || productId2
  const i18nParams = {
    zone: i18n.t(zone?.desc_key || `ZONE_${zoneId}`),
    product1: i18n.t(`PRODUCT_${descKey1}`),
    product2: i18n.t(`PRODUCT_${descKey2}`),
    percentage: Math.floor(pricingRatio * 100),
  }

  if (weight) {
    i18nParams.weight = typeof weight === 'number' ? `${weight} kg` : weight
  }

  return i18nParams
}

const getI18nZonesParams = (zoneId1, zoneId2, weight, zoneList) => {
  const zone1 = zoneList?.find(z => z.id === zoneId1)
  const zone2 = zoneList?.find(z => z.id === zoneId2)
  const i18nParams = {
    zone1: i18n.t(zone1?.desc_key || `ZONE_${zoneId1}`),
    zone2: i18n.t(zone2?.desc_key || `ZONE_${zoneId2}`),
    weight: typeof weight === 'number' ? `${weight} kg` : weight,
  }

  return i18nParams
}

const getZonePrices = (zoneId, productTable) => {
  const zonePrices = {}
  for (const column of productTable.kgRows) {
    zonePrices[column.weight] = column[zoneId]
  }
  return zonePrices
}

const validateZonePrices = (zonePrices, validateError) => {
  const zonePricesTuples = Object.entries(zonePrices)

  for (let i = 1; i < zonePricesTuples.length; i++) {
    const [weight, price] = zonePricesTuples[i]
    const prevWeightPrice = zonePricesTuples[i - 1][1]

    if (Number(prevWeightPrice) > Number(price)) {
      const tooltipTextError = i18n.t('SHIPPING_TOOLTIP_WEIGHT_ERROR')

      addToObjectKey(validateError, weight, 'zone', tooltipTextError)
    }
  }
}

const compliesWithZoningRules = (zoningRules, kgPricesPerZone, zoneId) => {
  if (!(zoneId in zoningRules)) {
    return true
  }

  const { comparedZone, allowEqual, requireEqual } = zoningRules[zoneId]

  const currentZonePrice = kgPricesPerZone[zoneId]
  const comparedZonePrice = kgPricesPerZone[comparedZone]

  let isValid
  if (requireEqual) {
    isValid = Number(currentZonePrice) === Number(comparedZonePrice)
  } else if (allowEqual) {
    isValid = Number(currentZonePrice) >= Number(comparedZonePrice)
  } else {
    isValid = Number(currentZonePrice) > Number(comparedZonePrice)
  }

  return isValid
}

const validateZoningRulesPerWeight = (validationsParams, zoningRules) => {
  const { zoneId, productTable, zoneErrors } = validationsParams

  if (!(zoneId in zoningRules)) {
    return zoneErrors
  }

  const { kgRows } = productTable
  const { comparedZone, allowEqual } = zoningRules[zoneId]

  for (const kgPricesPerZone of kgRows) {
    if (!compliesWithZoningRules(zoningRules, kgPricesPerZone, zoneId)) {
      const messageKey = allowEqual
        ? 'SHIPPING_TOOLTIP_ZONE_ERROR_ALLOW_EQUAL'
        : 'SHIPPING_TOOLTIP_ZONE_ERROR'

      const invalidStrMsgError = i18n.t(
        messageKey,
        getI18nZonesParams(zoneId, comparedZone, kgPricesPerZone.weight, validationsParams.zoneList),
      )

      addToObjectKey(zoneErrors, kgPricesPerZone.weight, 'zone', invalidStrMsgError)
    }
  }

  return zoneErrors
}

const validateZoningRulesAdditionalKg = (validationsParams, zoningRules) => {
  const { zoneId, productTable, zoneErrors } = validationsParams

  if (!(zoneId in zoningRules)) {
    return zoneErrors
  }

  const { addKilo } = productTable
  const { comparedZone, allowEqual } = zoningRules[zoneId]

  if (!compliesWithZoningRules(zoningRules, addKilo, zoneId)) {
    const messageKey = allowEqual
      ? 'SHIPPING_TOOLTIP_ZONE_ERROR_ALLOW_EQUAL'
      : 'SHIPPING_TOOLTIP_ZONE_ERROR'

    const invalidStrMsgError = i18n.t(
      messageKey,
      getI18nZonesParams(zoneId, comparedZone, addKilo.weight, validationsParams.zoneList),
    )

    addToObjectKey(zoneErrors, addKilo.weight, 'zone', invalidStrMsgError)
  }

  return zoneErrors
}

const validatePivotProduct = (validationsParams, percentage) => {
  const {
    zoneId,
    id,
    productTable,
    zoneErrors,
    comparedProductId,
    comparedProductTable,
    zoneList,
    GET_PRODUCT,
  } = validationsParams

  const comparedProductPricesByKg = {}

  comparedProductTable.kgRows.forEach(kgRow => {
    comparedProductPricesByKg[kgRow.weight] = kgRow
  })

  if (!productTable.kgRows) 
    return zoneErrors;

  for (const rowPrice of productTable.kgRows) {
    if (rowPrice.weight in comparedProductPricesByKg === false) continue; 

    const currentZonePrice = rowPrice[zoneId]
    const comparedZonePrice = comparedProductPricesByKg[rowPrice.weight][zoneId]
    const currentNum = Number(currentZonePrice)
    const comparedNum = Number(comparedZonePrice)

    if (!Number.isFinite(currentNum) || !Number.isFinite(comparedNum)) {
      continue;
    }

    const isValid = currentNum >= comparedNum * (1 + Number(percentage))


    if (isValid) continue;

    const i18nParams = getI18nProductsParams(
      zoneId,
      id,
      comparedProductId,
      percentage,
      rowPrice.weight,
      zoneList,
      GET_PRODUCT,
    )

    const messageKey =
      i18nParams.percentage > 0
        ? 'SHIPPING_TOOLTIP_PRODUCT_ERROR'
        : 'SHIPPING_TOOLTIP_PRODUCT_ERROR_NO_PERCENTAGE'

    const invalidStrMsgError = i18n.t(messageKey, i18nParams)

    addToObjectKey(zoneErrors, rowPrice.weight, 'product', invalidStrMsgError)
  }

  return zoneErrors
}

const validatePivotProductAdditionalKg = (validationsParams, percentage) => {
  const {
    zoneId,
    id,
    productTable,
    zoneErrors,
    comparedProductId,
    comparedProductTable,
    zoneList,
    GET_PRODUCT,
  } = validationsParams

  const { addKilo } = productTable

  const currentZonePrice = addKilo[zoneId]
  const comparedZonePrice = comparedProductTable.addKilo[zoneId]
  const currentNum = Number(currentZonePrice)
  const comparedNum = Number(comparedZonePrice)

  if (!Number.isFinite(currentNum) || !Number.isFinite(comparedNum)) {
    return zoneErrors;
  }

  const isValid = currentNum >= comparedNum * (1 + Number(percentage))

  if (isValid) 
    return zoneErrors;


  const i18nParams = getI18nProductsParams(
    zoneId,
    id,
    comparedProductId,
    percentage,
    addKilo.weight,
    zoneList,
    GET_PRODUCT,
  )

  const messageKey =
    i18nParams.percentage > 0
      ? 'SHIPPING_TOOLTIP_PRODUCT_ERROR'
      : 'SHIPPING_TOOLTIP_PRODUCT_ERROR_NO_PERCENTAGE'

  const invalidStrMsgError = i18n.t(messageKey, i18nParams)

  addToObjectKey(zoneErrors, addKilo.weight, 'product', invalidStrMsgError)
  

  return zoneErrors
}

export const getZoneErrors = (zoneId, context) => {
  const {
    id,
    productTable,
    zoningRules,
    hasPivotProductRules,
    pivotProductRules,
    GET_PRODUCT_TABLE,
  } = context

  const zoneErrors = {}

  const zonePrices = getZonePrices(zoneId, productTable)

  validateZonePrices(zonePrices, zoneErrors)

  const validationsParams = {
    id,
    zoneId,
    productTable,
    zoneErrors,
    zoneList: context.GET_PRODUCT?.(id)?.zoneList,
    GET_PRODUCT: context.GET_PRODUCT,
  }

  validateZoningRulesPerWeight(validationsParams, zoningRules)

  validateZoningRulesAdditionalKg(validationsParams, zoningRules)

  if (hasPivotProductRules) {
    const extraPricingProductIds = Object.keys(pivotProductRules.extraPricingRatio)

    if (extraPricingProductIds.length) {
      for (let i = 0; i < extraPricingProductIds.length; i++) {
        const comparedProductId = extraPricingProductIds[i]

        validationsParams.comparedProductId = comparedProductId
        validationsParams.comparedProductTable = GET_PRODUCT_TABLE(comparedProductId)

        const percentage = pivotProductRules.extraPricingRatio[comparedProductId]

        validatePivotProduct(validationsParams, percentage)

        if (productTable.addKilo) {
          validatePivotProductAdditionalKg(validationsParams, percentage)
        }
      }
    }

    const belowPricingProductIds = Object.keys(pivotProductRules.belowPricingRatio)

    if (belowPricingProductIds.length) {
      for (let i = 0; i < belowPricingProductIds.length; i++) {
        const comparedProductId = belowPricingProductIds[i]

        const inversedValidationsParams = {
          zoneId,
          zoneErrors,
          id: comparedProductId,
          comparedProductId: id,
          productTable: GET_PRODUCT_TABLE(comparedProductId),
          comparedProductTable: productTable,
          zoneList: context.GET_PRODUCT?.(comparedProductId)?.zoneList,
          GET_PRODUCT: context.GET_PRODUCT,
        }

        const percentage = pivotProductRules.belowPricingRatio[comparedProductId]

        validatePivotProduct(inversedValidationsParams, percentage)

        if (productTable.addKilo) {
          validatePivotProductAdditionalKg(inversedValidationsParams, percentage)
        }
      }
    }
  }

  return zoneErrors
}
